import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/roles";
import { STAGE_COLORS } from "@/lib/workflow";
import { fmtDate, fmtHM } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Layers, CheckCircle2, AlertTriangle, CalendarClock, Eye, Stamp,
  LinkIcon, Share2, Users, Clock, Building2, GraduationCap,
} from "lucide-react";

const STAGES = ["To Do", "Prepare", "Create", "Review", "Approval", "Complete"];
const CLOSED = ["Not Used", "Rejected", "Cancelled", "Internal Use"];

function pad(n) { return String(n).padStart(2, "0"); }
function dstr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function periodRange(period, custom) {
  const now = new Date();
  let start, end;
  if (period === "day") { start = new Date(now); end = new Date(now); }
  else if (period === "week") {
    const day = (now.getDay() + 6) % 7;
    start = new Date(now); start.setDate(now.getDate() - day);
    end = new Date(start); end.setDate(start.getDate() + 6);
  } else if (period === "custom") {
    start = custom.start ? new Date(custom.start + "T00:00:00") : new Date(2000, 0, 1);
    end = custom.end ? new Date(custom.end + "T00:00:00") : now;
  } else { start = new Date(now.getFullYear(), now.getMonth(), 1); end = new Date(now.getFullYear(), now.getMonth() + 1, 0); }
  start.setHours(0, 0, 0, 0); end.setHours(23, 59, 59, 999);
  return { start, end, startStr: dstr(start), endStr: dstr(end) };
}
function daysUntil(iso) { if (!iso) return null; return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000); }

function Kpi({ icon: Icon, label, value, accent, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`text-left rounded-xl border border-border bg-card p-5 transition-all ${onClick ? "hover:border-primary/40 hover:shadow-md cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
      <p className="font-display font-extrabold text-3xl mt-3 tabular-nums">{value}</p>
    </button>
  );
}

function PeriodPicker({ period, setPeriod, custom, setCustom }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={period} onValueChange={setPeriod}>
        <SelectTrigger data-testid="dashboard-period" className="w-36 h-9 text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="day">This Day</SelectItem>
          <SelectItem value="week">This Week</SelectItem>
          <SelectItem value="month">This Month</SelectItem>
          <SelectItem value="custom">Custom</SelectItem>
        </SelectContent>
      </Select>
      {period === "custom" && (
        <>
          <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-9 w-40" data-testid="dashboard-custom-start" />
          <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-9 w-40" data-testid="dashboard-custom-end" />
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const admin = isAdmin(user);
  const [assets, setAssets] = useState([]);
  const [posts, setPosts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [period, setPeriod] = useState("month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const { start, end, startStr, endStr } = periodRange(period, custom);

  useEffect(() => {
    Promise.all([
      api.get("/assets").then((r) => r.data).catch(() => []),
      api.get("/posts").then((r) => r.data).catch(() => []),
      admin ? api.get("/users").then((r) => r.data).catch(() => []) : Promise.resolve([]),
    ]).then(([a, p, u]) => { setAssets(a || []); setPosts(p || []); setUsers(u || []); setLoaded(true); });
  }, [admin]);

  useEffect(() => {
    if (!admin) return;
    api.get("/timelogs", { params: { start: startStr, end: endStr } }).then((r) => setLogs(r.data || [])).catch(() => setLogs([]));
  }, [admin, startStr, endStr]);

  if (!loaded) return <div className="text-muted-foreground">Loading…</div>;

  const scope = admin ? assets : assets.filter((a) => (a.work_lead_id || a.owner_id) === user.user_id);
  const isActive = (a) => a.current_stage !== "Complete" && !CLOSED.includes(a.archive_status);
  const due = (a) => a.due_date || a.deadline;
  const inPeriod = (iso) => { if (!iso) return false; const t = new Date(iso).getTime(); return t >= start.getTime() && t <= end.getTime(); };

  const activeWork = scope.filter(isActive);
  const completedPeriod = scope.filter((a) => a.current_stage === "Complete" && inPeriod(a.completed_at));
  const overdue = activeWork.filter((a) => due(a) && daysUntil(due(a)) < 0);
  const dueThisWeek = activeWork.filter((a) => { const d = daysUntil(due(a)); return d !== null && d >= 0 && d <= 7; });
  const waitingReview = scope.filter((a) => a.current_stage === "Review");
  const waitingApproval = scope.filter((a) => a.current_stage === "Approval");
  const sourceMissing = scope.filter((a) => a.archive_status === "Source Link Missing");

  const stageCounts = STAGES.reduce((m, s) => {
    m[s] = scope.filter((a) => a.current_stage === s && (s === "Complete" || !CLOSED.includes(a.archive_status))).length;
    return m;
  }, {});

  // Employee-wise creative workflow (admin)
  const ownerIds = [...new Set(assets.map((a) => a.work_lead_id || a.owner_id).filter(Boolean))];
  const employeeRows = ownerIds.map((oid) => {
    const mine = assets.filter((a) => (a.work_lead_id || a.owner_id) === oid);
    const name = mine[0]?.work_lead_name || mine[0]?.owner_name || "Unassigned";
    const act = mine.filter(isActive);
    const byStage = STAGES.slice(0, 5).reduce((m, s) => { m[s] = act.filter((a) => a.current_stage === s).length; return m; }, {});
    return {
      id: oid, name, active: act.length, byStage,
      completed: mine.filter((a) => a.current_stage === "Complete" && inPeriod(a.completed_at)).length,
      overdue: act.filter((a) => due(a) && daysUntil(due(a)) < 0).length,
      dueSoon: act.filter((a) => { const d = daysUntil(due(a)); return d !== null && d >= 0 && d <= 3; }).length,
    };
  }).sort((a, b) => b.active - a.active);

  // Social
  const st = (p) => (p.status || "").toLowerCase();
  const schedDate = (p) => p.scheduled_at || p.scheduled_date || p.scheduled_for;
  const myPosts = admin ? posts : posts.filter((p) => (p.owner_id || p.created_by) === user.user_id);
  const socialInPeriod = myPosts.filter((p) => inPeriod(schedDate(p)) || inPeriod(p.published_at));
  const social = {
    planned: socialInPeriod.filter((p) => st(p) === "plan" || st(p) === "planned").length,
    scheduled: socialInPeriod.filter((p) => st(p) === "scheduled").length,
    published: socialInPeriod.filter((p) => st(p) === "published").length,
    upcoming: socialInPeriod.filter((p) => st(p) === "scheduled" && daysUntil(schedDate(p)) >= 0).length,
    missed: socialInPeriod.filter((p) => st(p) !== "published" && schedDate(p) && daysUntil(schedDate(p)) < 0).length,
  };
  const platforms = [...new Set(myPosts.map((p) => p.platform).filter(Boolean))];
  const platformRows = platforms.map((pl) => {
    const rows = socialInPeriod.filter((p) => p.platform === pl);
    return {
      platform: pl,
      planned: rows.filter((p) => st(p) === "plan" || st(p) === "planned").length,
      scheduled: rows.filter((p) => st(p) === "scheduled").length,
      upcoming: rows.filter((p) => st(p) === "scheduled" && daysUntil(schedDate(p)) >= 0).length,
      missed: rows.filter((p) => st(p) !== "published" && schedDate(p) && daysUntil(schedDate(p)) < 0).length,
      published: rows.filter((p) => st(p) === "published").length,
    };
  });

  // Daily Work (admin) — from timelogs within the period
  const activeUsers = users.filter((u) => u.status === "active");
  const present = logs.filter((l) => l.presence !== "leave");
  const dailyWork = {
    present: [...new Set(present.map((l) => l.user_id))].length,
    leave: [...new Set(logs.filter((l) => l.presence === "leave").map((l) => l.user_id))].length,
    noActivity: activeUsers.filter((u) => !logs.some((l) => l.user_id === u.user_id)).length,
    total: present.length,
    completed: present.filter((l) => l.status === "completed").length,
    inProgress: present.filter((l) => l.status === "in_progress").length,
    office: present.filter((l) => l.location === "Office").length,
    field: present.filter((l) => l.location === "Field" || l.location === "School").length,
    seconds: present.reduce((s, l) => s + (l.active_seconds || 0), 0),
  };
  const empActivity = activeUsers.map((u) => {
    const mine = logs.filter((l) => l.user_id === u.user_id);
    const pr = mine.filter((l) => l.presence !== "leave");
    return {
      id: u.user_id, name: u.name,
      onLeave: mine.some((l) => l.presence === "leave"),
      total: pr.length,
      completed: pr.filter((l) => l.status === "completed").length,
      inProgress: pr.filter((l) => l.status === "in_progress").length,
      seconds: pr.reduce((s, l) => s + (l.active_seconds || 0), 0),
      outcomes: pr.filter((l) => l.status === "completed" && l.outcome).map((l) => l.outcome),
    };
  });

  const TONE = { rose: "bg-rose-500/10 text-rose-600", amber: "bg-amber-500/10 text-amber-600", orange: "bg-orange-500/10 text-orange-600" };
  const attention = [
    ...overdue.map((a) => ({ a, reason: "Overdue", tone: "rose" })),
    ...waitingApproval.map((a) => ({ a, reason: "Waiting for approval", tone: "amber" })),
    ...waitingReview.map((a) => ({ a, reason: "Waiting for review", tone: "orange" })),
    ...sourceMissing.map((a) => ({ a, reason: "Source link missing", tone: "orange" })),
  ].slice(0, 12);

  const Th = ({ children, right }) => <th className={`font-semibold px-3 py-2.5 ${right ? "text-right" : "text-left"} whitespace-nowrap`}>{children}</th>;

  return (
    <div className="fade-up space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold">{admin ? "Dashboard" : `Hi ${(user?.name || "").split(" ")[0]}`}</h2>
          <p className="text-sm text-muted-foreground">{admin ? "Team & system overview" : "Your work at a glance"}</p>
        </div>
        <PeriodPicker period={period} setPeriod={setPeriod} custom={custom} setCustom={setCustom} />
      </div>

      {/* TEAM & SYSTEM OVERVIEW */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Team &amp; System Overview — what needs attention?</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-active-work" icon={Layers} label={admin ? "Active Work" : "My Active Work"} value={activeWork.length} accent="bg-blue-500/10 text-blue-500" onClick={() => navigate("/workflow?flag=active")} />
          <Kpi testid="kpi-completed-month" icon={CheckCircle2} label="Completed" value={completedPeriod.length} accent="bg-emerald-500/10 text-emerald-500" />
          <Kpi testid="kpi-overdue" icon={AlertTriangle} label={admin ? "Overdue" : "My Overdue"} value={overdue.length} accent="bg-rose-500/10 text-rose-500" onClick={() => navigate("/workflow?flag=overdue")} />
          <Kpi testid="kpi-due-week" icon={CalendarClock} label="Due This Week" value={dueThisWeek.length} accent="bg-amber-500/10 text-amber-500" />
          <Kpi testid="kpi-review" icon={Eye} label="Waiting For Review" value={waitingReview.length} accent="bg-orange-500/10 text-orange-500" onClick={() => navigate("/workflow?stage=Review")} />
          <Kpi testid="kpi-approval" icon={Stamp} label="Waiting For Approval" value={waitingApproval.length} accent="bg-amber-500/10 text-amber-600" onClick={() => navigate("/workflow?stage=Approval")} />
          <Kpi testid="kpi-source-missing" icon={LinkIcon} label="Source Link Missing" value={sourceMissing.length} accent="bg-orange-500/10 text-orange-500" onClick={() => navigate("/workflow?flag=source-missing")} />
          <Kpi testid="kpi-social-upcoming" icon={Share2} label="Social Upcoming" value={social.upcoming} accent="bg-violet-500/10 text-violet-500" onClick={() => navigate("/calendar?condition=upcoming")} />
        </div>
      </div>

      {/* CREATIVE WORKFLOW */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="font-display font-semibold mb-4">Creative Workflow</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAGES.map((s) => {
            const sc = STAGE_COLORS[s] || STAGE_COLORS["To Do"];
            return (
              <button key={s} data-testid={`stage-card-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => navigate(`/workflow?stage=${encodeURIComponent(s)}`)} className="text-left rounded-lg border border-border p-3 hover:border-primary/40 transition-colors">
                <div className={`h-1 w-full rounded-full ${sc.bar} mb-2`} />
                <div className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${sc.dot}`} /><span className="text-xs font-medium">{s}</span></div>
                <p className="font-display font-extrabold text-2xl mt-1 tabular-nums">{stageCounts[s]}</p>
              </button>
            );
          })}
        </div>

        {admin && employeeRows.length > 0 && (
          <div className="mt-5 overflow-x-auto" data-testid="employee-workflow-table">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">By Employee</p>
            <table className="w-full text-sm min-w-[900px]">
              <thead><tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <Th>Employee</Th><Th right>Active</Th><Th right>To Do</Th><Th right>Prepare</Th><Th right>Create</Th><Th right>Review</Th><Th right>Approval</Th><Th right>Completed</Th><Th right>Overdue</Th><Th right>Due Soon</Th>
              </tr></thead>
              <tbody>
                {employeeRows.map((r) => (
                  <tr key={r.id} data-testid={`emp-row-${r.id}`} onClick={() => navigate(`/workflow?lead=${r.id}`)} className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer">
                    <td className="px-3 py-2.5 font-medium whitespace-nowrap">{r.name}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{r.active}</td>
                    {STAGES.slice(0, 5).map((s) => <td key={s} className="px-3 py-2.5 text-right font-mono">{r.byStage[s]}</td>)}
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600">{r.completed}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-600">{r.overdue}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-600">{r.dueSoon}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Needs attention */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="font-display font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Needs attention</p>
        {attention.length === 0 ? (
          <p className="text-sm text-muted-foreground">All clear — nothing needs attention.</p>
        ) : (
          <div className="space-y-2">
            {attention.map(({ a, reason, tone }) => (
              <div key={a.asset_id + reason} data-testid={`attention-${a.asset_id}`} onClick={() => navigate("/workflow?flag=overdue")} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 cursor-pointer hover:border-primary/40">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{a.work_item || a.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.work_item_id} · {a.work_lead_name || a.owner_name} · {a.current_stage}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${TONE[tone]}`}>{reason}{due(a) ? ` · ${fmtDate(due(a))}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SOCIAL MEDIA */}
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="font-display font-semibold mb-4 flex items-center gap-2"><Share2 className="h-4 w-4 text-violet-500" /> Social Media</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[["Planned", social.planned, "text-violet-600"], ["Scheduled", social.scheduled, "text-blue-600"], ["Published", social.published, "text-emerald-600"], ["Upcoming", social.upcoming, "text-amber-600"], ["Missed / Overdue", social.missed, "text-rose-600"]].map(([label, val, c]) => (
            <div key={label} data-testid={`social-${String(label).split(" ")[0].toLowerCase()}`} className="rounded-lg border border-border p-3">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className={`font-display font-extrabold text-2xl mt-1 tabular-nums ${c}`}>{val}</p>
            </div>
          ))}
        </div>
        {platformRows.length > 0 && (
          <div className="mt-5 overflow-x-auto" data-testid="platform-social-table">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">By Platform</p>
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <Th>Platform</Th><Th right>Planned</Th><Th right>Scheduled</Th><Th right>Upcoming</Th><Th right>Missed / Overdue</Th><Th right>Published</Th>
              </tr></thead>
              <tbody>
                {platformRows.map((r) => (
                  <tr key={r.platform} data-testid={`platform-row-${r.platform}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2.5 font-medium whitespace-nowrap">{r.platform}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.planned}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.scheduled}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-600">{r.upcoming}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-600">{r.missed}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600">{r.published}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DAILY WORK (admin) */}
      {admin && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="daily-work-section">
          <p className="font-display font-semibold mb-4 flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Daily Work</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[["Present", dailyWork.present], ["On Leave", dailyWork.leave], ["No Activity", dailyWork.noActivity], ["Total Activities", dailyWork.total], ["Completed", dailyWork.completed], ["In Progress", dailyWork.inProgress], ["Office", dailyWork.office], ["Field", dailyWork.field], ["Active Time", fmtHM(dailyWork.seconds)]].map(([label, val]) => (
              <div key={label} data-testid={`dailywork-${String(label).split(" ")[0].toLowerCase()}`} className="rounded-lg border border-border p-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className="font-display font-extrabold text-xl mt-1 tabular-nums">{val}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 overflow-x-auto">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">By Employee</p>
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <Th>Employee</Th><Th>Status</Th><Th right>Activities</Th><Th right>Completed</Th><Th right>In Progress</Th><Th right>Active Time</Th><Th>Latest Outcome</Th>
              </tr></thead>
              <tbody>
                {empActivity.map((r) => (
                  <tr key={r.id} data-testid={`dailywork-row-${r.id}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2.5 font-medium whitespace-nowrap">{r.name}</td>
                    <td className="px-3 py-2.5">{r.onLeave ? <span className="text-amber-600 text-xs font-semibold">LEAVE</span> : r.total > 0 ? <span className="text-emerald-600 text-xs font-semibold">Active</span> : <span className="text-muted-foreground text-xs">No activity</span>}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{r.total}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600">{r.completed}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-blue-600">{r.inProgress}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtHM(r.seconds)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground max-w-[220px] truncate">{r.outcomes[r.outcomes.length - 1] || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
